import { ResolutionsEnum } from "@/main/services/enum/ResolutionsEnum";
import { getTranslateByKey, isNullUndefined, useResponsiveTable } from "@/main/services/utils";
import { Paper, TableBody, TableCell, TableContainerProps, TableHead, TableProps, TableRow } from "@mui/material";
import { createStyles, withStyles } from "@mui/styles";
import React from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import LoadingComponent from "../loading/loadingComponent";
import * as S from "./styles";

interface IBoxProps extends TableContainerProps {
	title?: string;
	maxHeight?: string;
	marginCustom?: string;
	typeGrid?: "oddEven" | "default" | "backgroundHeader" | "backgroundBody";
	sizeCell?: "small" | "medium" | "large";
	textAlign?: "center" | "left" | "right";
	rowHeaders?: string[] | React.ReactNode[];
	centerLastColumn?: boolean;
	children: React.ReactNode;
	propsTable?: TableProps;
	showEndMessage?: boolean;
	infiniteScrollConfig?: {
		hasMore: boolean;
		fetchData: () => void;
		dataLength: number;
		isLoading: boolean;
		idScrollTarget?: string;
	} | null;
	sizeColumns?: string[];
	widthCustom?: string;
	breakpointTable?: number;
}

const StyledTableCell = withStyles(() =>
	createStyles({
		head: {
			color: "#7e7e7e",
			fontWeight: "bold",
		},
		body: {
			fontSize: 14,
			color: "#000",
		},
	}),
)(TableCell);

function BoxGrid({
	title,
	maxHeight = "",
	marginCustom = "",
	rowHeaders,
	children,
	infiniteScrollConfig,
	sizeCell = "large",
	typeGrid = "default",
	textAlign = "left",
	centerLastColumn = false,
	propsTable,
	sizeColumns,
	widthCustom,
	showEndMessage,
	breakpointTable = ResolutionsEnum.tablet,
	...props
}: IBoxProps) {
	const { totalWidthPercent, totalWidthPX } = useResponsiveTable(sizeColumns);

	function renderGrid() {
		return (
			<S.StyledTableContainer id="id-general-boxgrid" component={Paper} $maxHeight={maxHeight} {...props}>
				<S.StyledTable
					{...propsTable}
					$typeGrid={typeGrid}
					$sizeCell={sizeCell}
					$isFixedTable={!isNullUndefined(sizeColumns)}
					$breakpointTable={breakpointTable}
					aria-label="customized table"
				>
					{!isNullUndefined(sizeColumns) && (
						<colgroup>
							{sizeColumns.map((size, index) => (
								<col
									key={index}
									style={{
										width: size === "responsive" ? `calc(100% - ${totalWidthPercent}% - ${totalWidthPX}px)` : size,
									}}
								/>
							))}
						</colgroup>
					)}
					<TableHead>
						<TableRow>
							{rowHeaders &&
								rowHeaders.map((header, index) =>
									index === 0 ? (
										<StyledTableCell key={index}>{header}</StyledTableCell>
									) : index === rowHeaders.length - 1 && centerLastColumn === true ? (
										<StyledTableCell key={index} style={{ textAlign: "center" }}>
											{header}
										</StyledTableCell>
									) : (
										<StyledTableCell key={index} style={{ textAlign }}>
											{header}
										</StyledTableCell>
									),
								)}
						</TableRow>
					</TableHead>
					<TableBody>{children}</TableBody>
				</S.StyledTable>
			</S.StyledTableContainer>
		);
	}

	return (
		<S.Container marginCustom={marginCustom} widthCustom={widthCustom}>
			{title && <S.Title>{title}</S.Title>}

			{infiniteScrollConfig ? (
				<div
					style={{
						height: infiniteScrollConfig.idScrollTarget ? "auto" : "95%",
					}}
				>
					<InfiniteScroll
						style={{ overflowX: "hidden", paddingBottom: "2px" }}
						dataLength={infiniteScrollConfig.dataLength}
						next={infiniteScrollConfig.fetchData}
						hasMore={infiniteScrollConfig.hasMore}
						scrollableTarget={infiniteScrollConfig.idScrollTarget}
						loader={infiniteScrollConfig.isLoading ? <LoadingComponent /> : <></>}
						endMessage={
							showEndMessage ? (
								<S.EndMessage className="d-flex d-flex justify-content-center">
									{getTranslateByKey("information.allResultsWereDisplayed")}
								</S.EndMessage>
							) : null
						}
					>
						{renderGrid()}
					</InfiniteScroll>
				</div>
			) : (
				renderGrid()
			)}
		</S.Container>
	);
}

export default BoxGrid;
